import { BreadcrumbJsonLd, NextSeo } from 'next-seo';
import dynamic from 'next/dynamic';
import { useEffect } from 'react';
import { useEntity } from '../../../../lib/entity';
import authFetch from '../../../../lib/utils/authFetch';
import getIdFromSlug from '../../../../lib/utils/getIdFromSlug';
import slugify from '../../../../lib/utils/slugify';
import truncateString from '../../../../lib/utils/truncateString';

const Entity = dynamic(() => import('../../../../components/Entities/Entity'), {
  ssr: false,
});

function EntityPage({ type, entity }) {
  const addEntity = useEntity().addEntity;
  const dispatch = useEntity().dispatch;

  const title = truncateString(
    `Join the best discussions for ${
      entity.title
        ? `${entity.title} on ${process.env.NEXT_PUBLIC_APP_NAME}`
        : process.env.NEXT_PUBLIC_APP_NAME
    }`,
    62,
  );
  const description = truncateString(`${title}. ${process.env.NEXT_PUBLIC_APP_DESCRIPTION}`, 167);
  const descriptionMD = truncateString(
    `Join the best discussions for **${entity.title || ''}** on ${
      process.env.NEXT_PUBLIC_APP_DESCRIPTION_MD
    }.`,
    167,
  );

  useEffect(() => {
    if (entity.id) {
      addEntity(dispatch, entity);
    }
  }, [entity, addEntity, dispatch]);

  return (
    <>
      <NextSeo
        title={title}
        canonical={`${process.env.NEXT_PUBLIC_DOMAIN}e/${type}/${entity.slug}`}
        description={description}
        openGraph={{
          title,
          description,
          url: `${process.env.NEXT_PUBLIC_DOMAIN}e/${type}/${entity.slug}`,
          images: [
            {
              url: `${process.env.NEXT_PUBLIC_OG_URL}/${encodeURIComponent(
                descriptionMD,
              )}.png?theme=dark&md=1&fontSize=75px&widths=500&heights=700&images=${encodeURIComponent(
                `${process.env.NEXT_PUBLIC_DOMAIN}assets/svg/logo.svg`,
              )}${entity.photo ? `&images=${encodeURIComponent(entity.photo)}` : ''}`,
              width: 2048,
              height: 1170,
              alt: `${entity.title} Og Image`,
            },
          ],
        }}
      />
      <BreadcrumbJsonLd
        itemListElements={[
          {
            position: 1,
            name: 'Discover',
            item: `${process.env.NEXT_PUBLIC_DOMAIN}`,
          },
          {
            position: 2,
            name: title,
            item: `${process.env.NEXT_PUBLIC_DOMAIN}e/${type}/${entity.id}`,
          },
        ]}
      />
      <Entity />
    </>
  );
}

export async function getStaticProps({ params }) {
  const { type, entitySlug } = params;

  const entityId = getIdFromSlug(entitySlug);

  let entity = {};

  try {
    const getEntity = () => {
      if (
        ['undefined', '[type]'].includes(type) ||
        ['undefined', '[entityId]'].includes(entityId)
      ) {
        throw new Error('entityId or type must be provided');
      }
      return authFetch(`${process.env.NEXT_PUBLIC_API}/entity/${type}/${entityId}`);
    };

    entity = await getEntity();
    entity.slug = entitySlug;
  } catch (error) {
    console.error('🥵 ~ error', error);
    return {
      redirect: {
        destination: '/404',
        permanent: false,
      },
    };
  }

  return {
    props: {
      type,
      entity,
      analytics: {
        category: 'Entities',
        name: entity.title,
        path: `/entity/${type}/x`,
        url: `${process.env.NEXT_PUBLIC_DOMAIN}entity/${type}/x`,
      },
      revalidate: 1,
    },
  };
}

export async function getStaticPaths() {
  const entities = await authFetch(`${process.env.NEXT_PUBLIC_API}/trending`);
  const paths = entities.map((entity) => {
    const slug = slugify(entity.title);
    const entity_slug = `${slug}-${entity.id}`;
    return {
      params: {
        type: 'title',
        entitySlug: entity_slug,
      },
    };
  });
  return { paths, fallback: 'blocking' };
}

export default EntityPage;
